import { DndContext, DragOverlay, pointerWithin, useDraggable, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import { useLiveQuery } from "dexie-react-hooks";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { dueFromDateTime, toISODate, type Task } from "@todoer/shared";
import { moveDue } from "../components/CalendarView.js";
import { AddTaskButton, sortTasks, TaskRow, useTaskSensors } from "../components/TaskList.js";
import { Button, cx, IconButton, PageHeader } from "../components/ui.js";
import { db, useUser } from "../lib/db.js";
import { addDaysISO, dayDate, friendlyDate } from "../lib/format.js";
import { useNow } from "../lib/hooks.js";
import { cmd, dispatch } from "../lib/sync.js";

function DraggableRow({ task }: { task: Task }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });
  return <TaskRow task={task} showProject dnd={{ ref: setNodeRef, attributes, listeners, dragging: false, style: isDragging ? { opacity: 0.35 } : undefined }} />;
}

function DayHeader({ date, today, count, label }: { date: string; today: string; count: number; label?: string }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${date}` });
  const d = dayDate(date);
  return (
    <div ref={setNodeRef} className={cx("flex min-h-9 items-baseline gap-2 rounded-control border-b border-border pb-1.5 pt-1 transition-colors", isOver && "bg-brand-soft px-2")}>
      <h2 className={cx("text-heading font-semibold", date === today && "text-brand-text")}>{label ?? friendlyDate(date, today)}</h2>
      <span className="text-meta text-text-faint">{d.toLocaleDateString([], { weekday: date === today || date === addDaysISO(today, 1) ? "long" : undefined, day: "numeric", month: "short" })}</span>
      {count > 0 && <span className="tabular ml-auto text-meta text-text-faint">{count}</span>}
    </div>
  );
}

function EmptyRun({ from, to, today }: { from: string; to: string; today: string }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${from}` });
  const next = addDaysISO(to, 1);
  return (
    <div ref={setNodeRef} data-day-run={`${from}|${to}`}
      className={cx("flex h-10 items-center gap-3 rounded-control border border-dashed border-border px-3 text-meta text-text-faint transition-colors", isOver && "border-brand bg-brand-soft text-brand-text")}>
      <span className="h-1.5 w-1.5 rounded-full bg-border-strong" aria-hidden />
      {from === to ? `Nothing on ${friendlyDate(from, today)}` : `No tasks until ${dayDate(next).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })}`}
    </div>
  );
}

function StripDay({ date, today, count, active, onClick }: { date: string; today: string; count: number; active: boolean; onClick: () => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${date}`, disabled: date < today });
  const d = dayDate(date);
  const past = date < today;
  return (
    <button ref={setNodeRef} type="button" disabled={past} onClick={onClick} aria-label={`${d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}, ${count} task${count === 1 ? "" : "s"}`}
      aria-current={date === today ? "date" : undefined}
      className={cx("flex h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-card transition-colors disabled:opacity-35",
        active ? "bg-brand-soft text-brand-text" : "hover:bg-hover", isOver && "bg-brand-soft ring-2 ring-brand")}>
      <span className="text-caption font-medium uppercase">{d.toLocaleDateString([], { weekday: "short" })}</span>
      <span className={cx("tabular text-heading font-semibold", date === today && !active && "text-brand-text")}>{d.getDate()}</span>
      <span aria-hidden className={cx("tabular h-3 text-[10px] font-semibold leading-3", count ? "text-text-muted" : "text-transparent")}>{count || "·"}</span>
    </button>
  );
}

export function Upcoming() {
  const now = useNow();
  const today = toISODate(now);
  const user = useUser();
  const sow = user?.startOfWeek ?? 1;
  const [week, setWeek] = useState(0);
  const [activeDay, setActiveDay] = useState(today);
  const [dragId, setDragId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sensors = useTaskSensors();
  const tasks = useLiveQuery(() => db.tasks.filter((t) => !t.completedAt && !!t.due && t.due.date >= today).toArray(), [today]);

  const weekStart = useMemo(() => {
    const d = dayDate(today);
    d.setDate(d.getDate() - ((d.getDay() - sow + 7) % 7) + week * 7);
    return toISODate(d);
  }, [today, sow, week]);
  const strip = Array.from({ length: 7 }, (_, i) => addDaysISO(weekStart, i));
  const end = [addDaysISO(today, 27), strip[6]!].sort().pop()!;
  const byDay = useMemo(() => {
    const m = new Map<string, Task[]>();
    for (const t of tasks ?? []) m.set(t.due!.date, [...(m.get(t.due!.date) ?? []), t]);
    return m;
  }, [tasks]);
  const later = sortTasks((tasks ?? []).filter((t) => t.due!.date > end));

  // Days with tasks get a section; runs of empty days collapse into one row. Today always shows.
  const blocks: ({ kind: "day"; date: string } | { kind: "empty"; from: string; to: string })[] = [];
  for (let d = today; d <= end; d = addDaysISO(d, 1)) {
    if (d === today || byDay.get(d)?.length) blocks.push({ kind: "day", date: d });
    else {
      const last = blocks[blocks.length - 1];
      if (last?.kind === "empty") last.to = d; else blocks.push({ kind: "empty", from: d, to: d });
    }
  }

  const jump = (date: string) => {
    setActiveDay(date);
    const root = listRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-day="${date}"]`)
      ?? Array.from(root.querySelectorAll<HTMLElement>("[data-day-run]")).find((x) => { const [a, b] = x.dataset.dayRun!.split("|"); return date >= a! && date <= b!; });
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onDragEnd = (e: DragEndEvent) => {
    setDragId(null);
    const over = e.over ? String(e.over.id) : null;
    const task = (tasks ?? []).find((t) => t.id === String(e.active.id));
    if (!over?.startsWith("day:") || !task) return;
    const date = over.slice(4);
    if (date !== task.due?.date) void dispatch(cmd("item_update", { id: task.id, due: moveDue(task.due, date) }));
  };
  // Swipe the week strip sideways on touch screens to move a week.
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const onStripDown = (e: React.PointerEvent) => { if (e.pointerType !== "mouse") swipe.current = { x: e.clientX, y: e.clientY }; };
  const onStripUp = (e: React.PointerEvent) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(e.clientY - s.y)) setWeek((w) => Math.max(0, w + (dx < 0 ? 1 : -1)));
  };
  const dragTask = dragId ? (tasks ?? []).find((t) => t.id === dragId) : undefined;
  const monthLabel = dayDate(weekStart).toLocaleDateString([], { month: "long", year: "numeric" });

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={(e) => setDragId(String(e.active.id))} onDragCancel={() => setDragId(null)} onDragEnd={onDragEnd}>
      <PageHeader title="Upcoming" subtitle={monthLabel} actions={week !== 0 && <Button size="sm" onClick={() => { setWeek(0); jump(today); }}>Today</Button>} />
      <div className="sticky top-0 z-10 -mx-2 mb-4 flex items-center gap-1 border-b border-border bg-surface/90 px-2 pb-2 pt-1 backdrop-blur-xl phone:top-[calc(44px+env(safe-area-inset-top))]">
        <IconButton icon={ChevronLeft} label="Previous week" disabled={week <= 0} onClick={() => setWeek((w) => Math.max(0, w - 1))} className="phone:w-8" />
        <div className="flex min-w-0 flex-1 touch-pan-y gap-1" role="group" aria-label="Week" onPointerDown={onStripDown} onPointerUp={onStripUp} onPointerCancel={() => { swipe.current = null; }}>
          {strip.map((d) => <StripDay key={d} date={d} today={today} count={byDay.get(d)?.length ?? 0} active={d === activeDay} onClick={() => jump(d)} />)}
        </div>
        <IconButton icon={ChevronRight} label="Next week" onClick={() => setWeek((w) => w + 1)} className="phone:w-8" />
      </div>

      <div ref={listRef} className="space-y-6">
        {blocks.map((b) => b.kind === "day" ? (
          <section key={b.date} data-day={b.date} className="scroll-mt-28 phone:scroll-mt-40" aria-label={friendlyDate(b.date, today)}>
            <DayHeader date={b.date} today={today} count={byDay.get(b.date)?.length ?? 0} />
            <ul>{sortTasks(byDay.get(b.date) ?? []).map((t) => <DraggableRow key={t.id} task={t} />)}</ul>
            <AddTaskButton defaultDue={dueFromDateTime(b.date, null)} />
          </section>
        ) : <EmptyRun key={b.from} from={b.from} to={b.to} today={today} />)}
        {later.length > 0 && (
          <section aria-label="Later">
            <DayHeader date={addDaysISO(end, 1)} today={today} count={later.length} label="Later" />
            <ul>{later.map((t) => <DraggableRow key={t.id} task={t} />)}</ul>
          </section>
        )}
      </div>
      <DragOverlay>{dragTask && <div className="rounded-card border border-border bg-surface-raised px-3 py-2 text-body shadow-pop">{dragTask.content}</div>}</DragOverlay>
    </DndContext>
  );
}
